import { datadogRum } from '@datadog/browser-rum';
import { Button, Link, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductInfoItem from '@/components/ProductInfo/ProductInfoItem';
import { useQualityControlTool } from '@/context/Qualitycontrol.context';
import { environments } from '@/environments';
import { ProductType } from '@/types/ProductInfo';
import { getPriceFormatted } from '@/utils/helpers';
import CommentModal from './CommentModal';
import SectionTitle from './SectionTitle';
import ViewMoreText from '../Common/ViewMoreText/ViewMoreText';
import './ProductInfo.scss';

const InfoBlock = styled(Box)`
  padding: 1rem 0.5rem;
  width: 100%;
  display: flex;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const SellerTypeButton = styled(Button)(({ theme }) => ({
  borderColor: theme.palette.borderColor.light,
  borderRadius: '100px',
  backgroundColor: 'transparent',
  height: 'auto',
  lineHeight: 1,
  marginRight: '0.5rem',
  display: 'inline-block',
  textAlign: 'center',
  fontSize: '0.75rem',
  padding: '5px 8px',
  marginTop: '4px',
  minWidth: '50px',
  cursor: 'text',

  '&:hover': {
    borderColor: theme.palette.borderColor.main,
    backgroundColor: 'transparent',
  },
}));

export const ProductInfo = ({ whiteText }: { whiteText?: boolean }) => {
  const { t } = useTranslation();

  const { qcData: { product } = {} } = useQualityControlTool();

  // const { data: productComments } = useProductComments(uuid, product?.id);
  // const [showTranslatedComments, setShowTranslatedComments] = useState(true);

  const [openComments, setOpenComments] = useState(false);

  // const comments: JSX.Element[] = useMemo(() => {
  //   if (!productComments || !productComments.length) return [];

  //   return productComments.reduce((acc, curr, idx) => {
  //     if (curr.description && curr.customerEmail) {
  //       acc.push(
  //         <PublicComment
  //           key={idx}
  //           description={
  //             showTranslatedComments ? curr.translation : curr.description
  //           }
  //           commentAfterSale={curr.commentAfterSale}
  //           commentBadge={curr.commentBadge}
  //           createdAt={curr.createdAt}
  //           showDot={productComments.length > 1}
  //         />
  //       );
  //     }
  //     return acc;
  //   }, [] as JSX.Element[]);
  // }, [productComments, showTranslatedComments]);

  const handleOpenComments = () => setOpenComments(!openComments);

  if (!product || !product.id) return null;

  const productInfo = product as ProductType;

  const { description, comment: internalComment } = productInfo;

  const boProductLink = productInfo.id
    ? `${environments.legacyBoUrl}/admin.php?search=${productInfo.id}&EZBO_page=scan_qualite_v2&section=1`
    : '#';

  const fenxSellerInfoPage = `${environments.fenx}/profile/${product?.seller?.id}/?sortBy=relevance&tab=items-for-sale`;
  const c360UserInfoLink = `${environments.menu.boLink}/c360?hideheader=1#tab="login-id"&login-id-value=${product?.seller?.id}`;

  const sellerStatus = product.seller?.flagsV2?.filter(
    (item) => item.type === 'status'
  );

  const isNotForSale = !!product?.notForSaleReasons?.length;

  const whiteTextClasses = () =>
    (whiteText
      ? {
          '& .MuiTypography-root, .MuiButtonBase-root': {
            color: 'white',
          },
        }
      : {}) as any;

  return (
    <>
      <div className="productInfo_blocks-container">
        <InfoBlock className="productInfo_blocks-block">
          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={productInfo.brand}
            secondary={
              <>
                <Typography variant="body2" noWrap={false}>
                  {productInfo.category}
                </Typography>
                <Typography variant="body2">
                  {getPriceFormatted(productInfo.price)}
                </Typography>
              </>
            }
          />

          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={t('QC_V2.QC_LST.PRODUCT_ID')}
            secondary={
              <Link
                href={boProductLink}
                target="_blank"
                sx={{
                  textDecorationColor: (theme) => theme.palette.text.secondary,
                }}
              >
                {productInfo.id}
              </Link>
            }
          />

          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={t('QC_V2.QC_LST.PRODUCT_CONDITION')}
            secondary={productInfo.condition}
          />
        </InfoBlock>

        <InfoBlock className="productInfo_blocks-block—small">
          <ProductInfoItem
            sx={() => ({
              ...whiteTextClasses(),
              wordWrap: 'break-word',
            })}
            primary={t('QC_V2.MAIN.SELLER')}
            secondary={
              <>
                <Link
                  href={fenxSellerInfoPage}
                  target="_blank"
                  sx={(theme) => ({
                    textDecorationColor: theme.palette.text.secondary,
                  })}
                >
                  {!productInfo?.seller?.firstname &&
                  !productInfo?.seller?.lastname
                    ? '-'
                    : `${productInfo?.seller?.firstname}  ${productInfo?.seller?.lastname}`}
                </Link>
                <Link
                  href={c360UserInfoLink}
                  target="_blank"
                  sx={(theme) => ({
                    textDecorationColor: theme.palette.text.secondary,
                    display: 'block',
                  })}
                >
                  Seller BO
                </Link>
                <div>
                  {productInfo?.seller?.type && (
                    <SellerTypeButton
                      variant="outlined"
                      sx={() => ({ ...whiteTextClasses() })}
                    >
                      {productInfo.seller.type}
                    </SellerTypeButton>
                  )}
                  {sellerStatus?.length ? (
                    <SellerTypeButton
                      variant="outlined"
                      sx={() => ({ ...whiteTextClasses() })}
                    >
                      {sellerStatus[0].name}
                    </SellerTypeButton>
                  ) : null}
                </div>
              </>
            }
          />
          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={t('QC_V2.MAIN.SHIPPING_TO')}
            secondary={
              <>
                <div>{productInfo.deliveryCountry?.name || '-'}</div>
                <div>
                  {productInfo.isShippingInSameFiscalMarket !== null
                    ? productInfo.isShippingInSameFiscalMarket
                      ? `(${t('QC_V2.MAIN.INSIDE_CUSTOM_ZONE')})`
                      : `(${t('QC_V2.MAIN.OUTSIDE_CUSTOM_ZONE')})`
                    : ''}
                </div>
              </>
            }
          />
          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={t('QC_V2.MAIN.SERIAL_NUMBER')}
            secondary={productInfo.serialNumber || '-'}
          />

          {isNotForSale && (
            <ProductInfoItem
              sx={() => ({ ...whiteTextClasses() })}
              primary={t('QC_V2.MAIN.NOT_FOR_SALE')}
              secondary={
                <>
                  {productInfo.notForSaleReasons.map((item, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      color={(theme) =>
                        `${theme.palette.error.main} !important`
                      }
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {item}
                    </Typography>
                  ))}
                </>
              }
            />
          )}
        </InfoBlock>

        <InfoBlock>
          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={
              <SectionTitle
                title={t('QC_V2.MAIN.DESCRIPTION')}
                onClick={handleOpenComments}
              />
            }
            secondary={
              <Box mt={1}>
                <ViewMoreText
                  textClassName="productInfo_item-content"
                  buttonClassName="productInfo_item-viewMore"
                  text={productInfo.description || '-'}
                />
              </Box>
            }
          />
        </InfoBlock>

        {/* <InfoBlock>
          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '5px',
                  flexWrap: 'wrap',
                }}
              >
                <SectionTitle
                  title={t('QC_V2.MAIN.COMMENT')}
                  onClick={handleOpenComments}
                />
                <Button
                  onClick={() =>
                    setShowTranslatedComments(!showTranslatedComments)
                  }
                >
                  {showTranslatedComments
                    ? t('QC_V2.MAIN.SHOW_ORIGINAL_PRODUCT_COMMENT')
                    : t('QC_V2.MAIN.SHOW_TRANSLATED_PRODUCT_COMMENT')}
                </Button>
              </Box>
            }
            secondary={
              <Box mt={1.5}>
                <ViewMoreText
                  textClassName="productInfo_item-content"
                  buttonClassName="productInfo_item-viewMore"
                  text={
                    comments.length ? (
                      <Stack gap={1}>{comments.map((c) => c)}</Stack>
                    ) : (
                      '-'
                    )
                  }
                />
              </Box>
            }
          />
        </InfoBlock> */}

        <InfoBlock>
          <ProductInfoItem
            sx={() => ({ ...whiteTextClasses() })}
            primary={
              <SectionTitle
                title={t('QC_V2.MAIN.AUTH_CMT')}
                onClick={handleOpenComments}
              />
            }
            secondary={
              <ViewMoreText
                textClassName="productInfo_item-content"
                initiallyExpanded={true}
                buttonClassName="productInfo_item-viewMore"
                text={productInfo.comment || '-'}
              />
            }
          />
        </InfoBlock>
      </div>

      <CommentModal
        open={openComments}
        onClose={() => {
          handleOpenComments();
          datadogRum.addAction('Close modal comments modal');
        }}
        description={description}
        internalComment={internalComment}
        // publicComments={comments}
      />
    </>
  );
};
